import { LoadingState, BusyContent } from '../components/ui/Primitives';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import PasswordFields from '../components/PasswordFields';
import {
  acceptOrganizationInvitation,
  getOrganizationInvitation,
} from '../services/organizationApi';
import './Auth.css';
import './AcceptOrganizationInvitation.css';
import { passwordPolicyError } from '../utils/passwordPolicy';
import GoogleSignInButton from '../components/GoogleSignInButton';


function invitationError(error, fallback) {
  const data = error?.response?.data;
  if (typeof data?.error?.message === 'string') return data.error.message;
  if (typeof data?.description === 'string') return data.description;
  if (typeof data?.message === 'string') return data.message;
  if (error?.response?.status === 410) return 'This invitation has expired. Ask an organization administrator to resend it.';
  if (error?.response?.status === 409) return 'This invitation has already been used or revoked.';
  if (error?.response?.status === 404) return 'This invitation link is not valid.';
  return fallback;
}


function AcceptOrganizationInvitation({ login }) {
  const { invitationToken } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    let active = true;
    async function loadInvitation() {
      setIsLoading(true);
      setError('');
      try {
        const details = await getOrganizationInvitation(invitationToken);
        if (!active) return;
        setInvitation(details);
        setFirstName(details.firstName || '');
        setLastName(details.lastName || '');
      } catch (loadError) {
        if (active) setError(invitationError(loadError, 'This invitation could not be loaded.'));
      } finally {
        if (active) setIsLoading(false);
      }
    }
    if (invitationToken) loadInvitation();
    else {
      setError('This invitation link is not valid.');
      setIsLoading(false);
    }
    return () => { active = false; };
  }, [invitationToken]);

  const submit = async event => {
    event.preventDefault();
    const policyError = passwordPolicyError(password);
    if (policyError) {
      setPasswordError(policyError);
      setError(policyError);
      return;
    }
    if (password !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const result = await acceptOrganizationInvitation(invitationToken, {
        first_name: firstName,
        last_name: lastName,
        password,
      });
      login(result.token, false);
    } catch (submitError) {
      const message = invitationError(submitError, 'Your Sayable account could not be created.');
      if (/commonly used|data breach/i.test(message)) setPasswordError(message);
      setError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-content auth-page invitation-auth-page">
      <div className="auth-intro invitation-auth-intro">
        <BrandLogo variant="reversed" className="invitation-brand" decorative />
        <h1>Join your team on Sayable.</h1>
        <p>Use Google or create a password to access your organization’s shared workspace.</p>
        <div className="auth-proof"><strong>One workspace</strong><span>Calls, receptionist settings, and business information stay connected.</span></div>
      </div>
      <div className="auth-card invitation-auth-card">
        {isLoading ? (
          <LoadingState label="Opening your invitation…" />
        ) : invitation ? (
          <>
            <span className="invitation-eyebrow">Team invitation</span>
            <div className="page-title">Create your account</div>
            <p className="auth-card-subtitle">You’ve been invited to join <strong>{invitation.organizationName}</strong>.</p>
            {error ? <div className="invitation-error" role="alert">{error}</div> : null}
            <GoogleSignInButton purpose="invite" divider disabled={isSubmitting}
              payload={{ invitationToken, firstName, lastName }} onSuccess={result => login(result.token, false)} />
            <form onSubmit={submit}>
              <div className="invitation-name-fields">
                <label className="login-label" htmlFor="invitation-first-name">First name
                  <input id="invitation-first-name" value={firstName} onChange={event => setFirstName(event.target.value)} autoComplete="given-name" required disabled={isSubmitting} />
                </label>
                <label className="login-label" htmlFor="invitation-last-name">Last name
                  <input id="invitation-last-name" value={lastName} onChange={event => setLastName(event.target.value)} autoComplete="family-name" required disabled={isSubmitting} />
                </label>
              </div>
              <label className="login-label" htmlFor="invitation-email">Email
                <input id="invitation-email" type="email" value={invitation.email} autoComplete="email" readOnly />
              </label>
              <PasswordFields
                password={password}
                confirmPassword={confirmPassword}
                onPasswordChange={value => {
                  setPassword(value);
                  setPasswordError('');
                  setError('');
                }}
                onConfirmPasswordChange={setConfirmPassword}
                userInputs={[invitation.email, firstName, lastName, invitation.organizationName]}
                disabled={isSubmitting}
                idPrefix="invitation"
                serverError={passwordError}
              />
              <div className="invitation-access-note">
                <strong>{invitation.accessLevel === 'administrator' ? 'Organization administrator' : 'Team member'} access</strong>
                <span>This secure invitation can be used once.</span>
              </div>
              <button className="button login-button invitation-submit" type="submit" disabled={isSubmitting}>
                <BusyContent busy={isSubmitting} label={"Creating your account…"}>{'Create account and continue'}</BusyContent>
              </button>
            </form>
          </>
        ) : (
          <div className="invitation-unavailable">
            <span className="invitation-unavailable-icon" aria-hidden="true">!</span>
            <div className="page-title">Invitation unavailable</div>
            <p>{error || 'This invitation could not be opened.'}</p>
            <Link className="button invitation-signin-link" to="/signin">Go to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default AcceptOrganizationInvitation;
